/** @format */

import React from 'react';

import {Box} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {shortNumber} from 'helpers/numberHelpers';
import {diff} from 'features/mediaplan-progress/helpers/diff';
import {ratioPercent} from 'features/mediaplan-progress/helpers/ratioPercent';
import {type MetricKind, type MetricProgress} from 'features/mediaplan-progress/types';
import getTokenWithSpace from 'pages/Home/DigitalCast/helpers/getTokenWithSpace';

import {formatRemainder} from '../../formatRemainder';

import {getValueColor} from '../getValueColor';

import {
  DataCell,
  ForecastHeaderCell,
  HeaderCell,
  HeaderSubText,
  HeaderText,
  RemainderText,
  RowDivider,
  TableGrid,
  TableRoot,
  ValueText,
} from './styles';

export interface KpiRowModel {
  /**
   * DigitalCast chart id (e.g. 'cpl', 'clicks'). Carried for the dynamic chart + the
   * spend-row icon; not rendered by KpiTable itself.
   */
  chartId: string;
  label: string;
  kind: MetricKind;
  progress: MetricProgress;
  /**
   * true for cost-type KPIs (CPL, CPC, CPM, ДРР, budget) where lower values are
   * better. Caller supplies this from the widget's `isPositiveRed` flag.
   * When true, fact <= plan is GOOD (green); fact > plan is BAD (red).
   * When false (default), higher-is-better: fact >= plan → good, else bad.
   * Also drives the остаток sign: «−» always points at the bad direction —
   * fact − plan for higher-is-better, plan − fact for cost (overspend → «−»).
   */
  lowerIsBetter: boolean;
  /** false when <7 days of fact — the «Прогноз» value is neither computed nor shown (AS-7886). */
  showForecast: boolean;
  /** Optional unit token for value cells: '₽' (money), '%' (ДРР/CTR/ROMI…), undefined (counts). */
  unit?: string;
  /** Whether to put a space before the unit (true for '₽', false for '%'). */
  unitNeedsSpace?: boolean;
  /** Optional Material-Symbol name rendered before the label (e.g. 'currency_ruble' for Расход). */
  iconName?: string;
}

interface Props {
  rows: KpiRowModel[];
  planPeriodLabel: string;
}

const KpiTable = ({rows, planPeriodLabel}: Props) => {
  const {t} = useTranslation();

  if (rows.length === 0) {
    return null;
  }

  return (
    <TableRoot>
      <TableGrid>
        {/* Header row */}
        <HeaderCell>
          <HeaderText>
            <Trans>Показатель</Trans>
          </HeaderText>
        </HeaderCell>

        <HeaderCell>
          <HeaderText>{t('План на {{month}}', {month: planPeriodLabel})}</HeaderText>
        </HeaderCell>

        <HeaderCell>
          <HeaderText>
            <Trans>План</Trans>
          </HeaderText>
          <HeaderSubText>
            <Trans>на сегодня</Trans>
          </HeaderSubText>
        </HeaderCell>

        <HeaderCell>
          <HeaderText>
            <Trans>Факт</Trans>
          </HeaderText>
          <HeaderSubText>
            <Trans>на сегодня</Trans>
          </HeaderSubText>
        </HeaderCell>

        <HeaderCell>
          <HeaderText>
            <Trans>Выполнение</Trans>
          </HeaderText>
        </HeaderCell>

        <ForecastHeaderCell>
          <Box component='span' className='material-symbols-outlined'>
            event_upcoming
          </Box>
          <HeaderText>{t('Прогноз')}</HeaderText>
        </ForecastHeaderCell>

        {/* Data rows */}
        {rows.map(row => {
          const {
            chartId,
            label,
            kind,
            progress,
            lowerIsBetter,
            showForecast,
            unit,
            unitNeedsSpace,
            iconName,
          } = row;
          const {planForPeriod, planToDate, factToDate, forecastAtEnd} = progress;

          // Value cells carry the row's unit suffix: ' ₽' (money), '%' (ДРР/CTR…), '' (counts).
          const suffix = getTokenWithSpace({token: unit, needSpaceBeforeToken: unitNeedsSpace});
          const formatValue = (value: number) => `${shortNumber(value)}${suffix}`;

          // Выполнение: fact / planToDate expressed as percent
          const completionPct = ratioPercent(factToDate, planToDate);

          // Остаток: «−» always points at the bad direction. fact − plan for higher-is-better;
          // plan − fact for cost metrics (Расход/CPL…) so перерасход (fact > plan) renders «−».
          const remainderSign = lowerIsBetter ? -1 : 1;
          const factRemainder = remainderSign * diff({plan: planToDate, fact: factToDate});
          const forecastRemainder =
            remainderSign * diff({plan: planForPeriod, fact: forecastAtEnd});

          const valueColor = getValueColor(factToDate, planToDate, lowerIsBetter);
          const forecastColor = getValueColor(forecastAtEnd, planForPeriod, lowerIsBetter);

          return (
            <React.Fragment key={label}>
              <RowDivider />

              {/* Показатель (+ optional leading icon, e.g. ₽ for Расход) */}
              <DataCell sx={iconName ? {gap: 1} : undefined}>
                {iconName && (
                  <Box
                    component='span'
                    className='material-symbols-outlined'
                    sx={{
                      '&.material-symbols-outlined': {fontSize: '18px', lineHeight: '20px'},
                      color: theme => theme.palette.semantic_dark?.surface.onSurface,
                    }}
                  >
                    {iconName}
                  </Box>
                )}
                <ValueText sx={{color: theme => theme.palette.semantic_dark?.surface.onSurface}}>
                  {label}
                </ValueText>
              </DataCell>

              {/* План на месяц */}
              <DataCell>
                <ValueText sx={{color: theme => theme.palette.semantic_dark?.surface.onSurface}}>
                  {formatValue(planForPeriod)}
                </ValueText>
              </DataCell>

              {/* План на сегодня. Для производных (ratio: ДРР/CPL) план не накапливается —
                  «План на сегодня» совпадает с «Планом на месяц» (planForPeriod). Для additive —
                  накопленный к сегодня план (planToDate). */}
              <DataCell>
                <ValueText sx={{color: theme => theme.palette.semantic_dark?.surface.onSurface}}>
                  {formatValue(kind === 'additive' ? planToDate : planForPeriod)}
                </ValueText>
              </DataCell>

              {/* Факт на сегодня + remainder */}
              <DataCell sx={{gap: 1}}>
                <ValueText sx={{color: valueColor}}>{formatValue(factToDate)}</ValueText>
                <RemainderText data-testid={`row-fact-remainder-${chartId}`}>
                  {formatRemainder(factRemainder)}
                </RemainderText>
              </DataCell>

              {/* Выполнение */}
              <DataCell>
                <ValueText sx={{color: valueColor}}>{`${shortNumber(completionPct)}%`}</ValueText>
              </DataCell>

              {/* Прогноз + remainder — пусто, если <7 дней факта (прогноз не считаем). */}
              <DataCell sx={{gap: 1}}>
                {showForecast && (
                  <>
                    <ValueText sx={{color: forecastColor}}>{formatValue(forecastAtEnd)}</ValueText>
                    <RemainderText data-testid={`row-forecast-remainder-${chartId}`}>
                      {formatRemainder(forecastRemainder)}
                    </RemainderText>
                  </>
                )}
              </DataCell>
            </React.Fragment>
          );
        })}
      </TableGrid>
    </TableRoot>
  );
};

export {KpiTable};
